import React, { useState, useRef } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Avatar,
    Divider,
    Menu,
    MenuItem,
    ListItemIcon,
} from '@mui/material';
import { ArrowBackIcon, MoreHorizIcon, EditOutlinedIcon, SaveAltOutlinedIcon, EmailOutlinedIcon, DeleteOutlineIcon, OpenInNewIcon, AssignmentOutlinedIcon, PersonAddAlt1OutlinedIcon } from 'icons/materialIcons';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import avatarPlaceholder from 'images/avatar-cropped.png';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import { downloadVCard } from 'utils/vcfUtils';
import TeamRemoveLeadModal from './TeamRemoveLeadModal';
import TeamSendLeadEmailModal from './TeamSendLeadEmailModal';
import { deleteLead } from 'apis/leadCaptureApi';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import { useNavigate } from 'react-router-dom';

const TeamLeadDetailMenu = ({ lead, onBack, onEditClick, onDeleteComplete, onReassignClick, reassignButtonRef }) => {
    const navigate = useNavigate();
    const { deleteContact } = useLeadCaptureContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Function to handle email click
    const handleEmailClick = () => {
        handleMenuClose();
        setIsEmailModalOpen(true);
    };

    // Prepare lead data in the format expected by downloadVCard
    const prepareLeadData = () => {
        return {
            contact_info: {
                fullName: lead.leadName,
                email: lead.leadEmail,
                phoneNumber: lead.leadPhoneNumber,
                company: lead.leadCompany,
                jobTitle: lead.leadJobTitle,
                location: lead.location
            },
            bio: lead.bio,
            profilePictureUrl: lead.profilePictureUrl
        };
    };

    // Format display name - show only the name
    const displayName = lead?.leadName || lead?.leadEmail || 'Unknown';

    // Debug: Log the profile picture URL
    console.log('Lead profile picture URL:', lead?.profilePictureUrl);

    // Handle opening remove modal
    const handleRemoveClick = () => {
        handleMenuClose();
        setShowRemoveModal(true);
    };

    // Handle cancel remove
    const handleCancelRemove = () => {
        setShowRemoveModal(false);
    };

    // Handle confirm remove
    const handleConfirmRemove = async () => {
        try {
            setIsDeleting(true);
            await deleteLead(lead._id);
            deleteContact(lead._id);
            setShowRemoveModal(false);
            if (onDeleteComplete) {
                onDeleteComplete();
            }
        } catch (error) {
            console.error('Error deleting lead:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                    width: '100%'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={onBack}
                        sx={{
                            color: '#4f4f4f',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            height: 32,
                            my: 'auto'
                        }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={lead?.profilePictureUrl}
                            sx={{
                                width: 44,
                                height: 44,
                                background: 'linear-gradient(135deg, #bdc7d9 0%, #889bbd 100%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '16px'
                            }}
                            imgProps={{
                                loading: "eager",
                                referrerPolicy: "no-referrer",
                                crossOrigin: "anonymous",
                                onError: (e) => {
                                    e.target.style.display = 'none';
                                }
                            }}
                            onError={() => {
                                // Fallback handled by imgProps onError
                            }}
                        >
                            {(() => {
                                const name = lead?.leadName || '';
                                const nameParts = name.split(' ');
                                const initials = nameParts
                                    .map(part => part.charAt(0).toUpperCase())
                                    .slice(0, 2)
                                    .join('');
                                return initials;
                            })()}
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#242020' }}>
                            {displayName}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <DarkButtonWithIcon
                        text="Export to CRM"
                        icon={<PersonAddAlt1OutlinedIcon
                            sx={{
                                fontSize: 20,
                                color: '#fff'
                            }}
                        />}
                        iconPosition="start"
                        onClick={() => {
                            const leadData = prepareLeadData();
                            downloadVCard(leadData);
                        }}
                        sx={{
                            fontWeight: '700',
                            '& .MuiButton-startIcon': {
                                marginRight: '8px'
                            }
                        }}
                    />

                    <WhiteButtonWithIcon
                        icon={MoreHorizIcon}
                        text=""
                        onClick={handleMenuClick}
                        sx={{
                            width: '80px',
                            minWidth: '80px',
                            height: '40px',
                            padding: '0 16px',
                            borderRadius: '100px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #E0E0E0',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #E0E0E0'
                            }
                        }}
                        iconProps={{
                            fontSize: '20px',
                            sx: { color: '#4f4f4f' }
                        }}
                    />

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                borderRadius: '12px',
                                minWidth: 200,
                                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                            },
                        }}
                    >
                        {[
                            {
                                icon: EditOutlinedIcon,
                                text: 'Edit',
                                onClick: () => {
                                    handleMenuClose();
                                    if (onEditClick) onEditClick();
                                },
                                dataAttr: "data-edit-lead-button"
                            },
                            {
                                icon: SaveAltOutlinedIcon,
                                text: 'Save as contact',
                                onClick: () => {
                                    const leadData = prepareLeadData();
                                    downloadVCard(leadData);
                                    handleMenuClose();
                                }
                            },
                            {
                                icon: EmailOutlinedIcon,
                                text: 'Email Lead',
                                onClick: () => {
                                    handleMenuClose();
                                    setIsEmailModalOpen(true);
                                }
                            },
                            {
                                icon: LoopOutlinedIcon,
                                text: 'Reassign',
                                onClick: () => {
                                    handleMenuClose();
                                    if (onReassignClick) {
                                        onReassignClick();
                                    }
                                }
                            },
                            {
                                icon: DeleteOutlineIcon,
                                text: 'Remove',
                                sx: { color: 'error.main' },
                                onClick: handleRemoveClick
                            },
                        ].map((item, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    if (item.onClick) {
                                        item.onClick();
                                    } else {
                                        console.log(`${item.text} clicked`);
                                        handleMenuClose();
                                    }
                                }}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    ...item.sx,
                                }}
                                {...(item.dataAttr ? { [item.dataAttr]: true } : {})}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 36,
                                        ...(item.sx || {})
                                    }}
                                >
                                    <item.icon sx={{ fontSize: 20, ...(item.sx || {}) }} />
                                </ListItemIcon>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        ...item.sx
                                    }}
                                >
                                    {item.text}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Box>

            {/* Remove Lead Confirmation Modal */}
            <TeamRemoveLeadModal
                open={showRemoveModal}
                onClose={handleCancelRemove}
                onConfirm={handleConfirmRemove}
                isDeleting={isDeleting}
            />

            {/* Email Modal */}
            <TeamSendLeadEmailModal
                open={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                contact={lead}
            />
        </>
    );
};

export default TeamLeadDetailMenu;